// Sex estimation from teeth and jaws — pre→post educational-intervention KAP instrument.
// Drafted from the KS07 NotebookLM dossier (36 forensic-odontology sources; Sep 2026),
// then edited by the investigators. STARTER DRAFT pending expert validation (I-CVI).
//
// Terminology: current forensic literature prefers "sex estimation" (the result is
// probabilistic) and means biological sex, not gender identity. The study title keeps
// "Sex Determination" for continuity with the protocol; the items say "estimation".
//
// Edits made to the generated draft (keep this list until the expert panel signs off):
//  • KQ6 flipped to a positively-worded True item — the generated version ("universal
//    cut-off … across all global populations") was answerable from the absolute wording.
//  • KQ10 reworded: Barr-body microscopy is cytology, not a "molecular" method.
//  • V3 (MCI reporting) distractors rewritten — the generated ones said "100% certainty"
//    and "absolute", so the hedged correct option stood out.
//  • PQ7 now asks about complete dental records rather than charting palatal rugae/lip
//    prints, which this instrument teaches are weak evidence.
//  • Vignettes renumbered V1–V3 to match the other studies. The generated reserve item
//    (which tooth to sample from an incinerated jaw) and the three reserve knowledge
//    items (lip prints, reporting wording, pulpal DNA survival) are held for validation.
import { baseDemographics, TF, mc, same, NOT_YET_SEEING } from './common';

const demographics = [
  ...baseDemographics,
  { key: 'A4', field: 'prior_training', type: 'single', required: true,
    stem: 'Have you received any formal teaching on forensic odontology or sex estimation so far?',
    options: same('Yes', 'No', 'Not sure') },
  { key: 'A5', field: 'workshop', type: 'single', required: true,
    stem: 'Have you attended any workshop, seminar or CDE on forensic odontology?',
    options: same('Yes', 'No') },
  { key: 'A6', field: 'info_source', type: 'single', required: true,
    stem: 'Your main source of information on forensic odontology',
    options: same('Lectures/curriculum', 'Textbooks/guidelines', 'Seniors/peers', 'Internet/apps', 'Other') },
];

// 12 items: 5 true/false (3 True, 2 False) and 7 MCQs with the key spread a/b/b/c/c/d/d.
const knowledge = [
  { key: 'KQ1', type: 'single', options: TF,
    stem: 'Sex estimation from skeletal and dental remains gives a probability, not a definitive identification of the individual.' },
  { key: 'KQ2', type: 'single', stem: 'Which part of the adult skeleton gives the most accurate estimate of biological sex?',
    options: mc('The pelvis', 'The dentition', 'The skull', 'The mandible') },
  { key: 'KQ3', type: 'single', options: TF,
    stem: 'Skeletal morphology can reliably establish the sex of a child under ten years of age.' },
  { key: 'KQ4', type: 'single', stem: 'Which permanent tooth shows the greatest sexual dimorphism in mesiodistal crown width?',
    options: mc('Maxillary central incisor', 'Mandibular first molar', 'Maxillary second premolar', 'Mandibular canine') },
  { key: 'KQ5', type: 'single', stem: 'The mandibular canine index (MCI) is calculated as:',
    options: mc('Intercanine arch width divided by mesiodistal canine width',
      'Mesiodistal canine width divided by intercanine arch width',
      'Buccolingual canine width divided by mesiodistal canine width',
      'Mesiodistal canine width multiplied by intercanine arch width') },
  { key: 'KQ6', type: 'single', options: TF,
    stem: 'Mandibular canine index standards have to be re-established for each population before they are used.' },
  { key: 'KQ7', type: 'single', stem: 'Which non-metric crown trait shows the most marked sexual dimorphism?',
    options: mc('Cusp of Carabelli', 'Shovel-shaped incisors', 'Canine distal accessory ridge', 'Enamel pearls') },
  { key: 'KQ8', type: 'single', options: TF,
    stem: 'A square or bilobate chin is more characteristic of male than of female mandibles.' },
  { key: 'KQ9', type: 'single', stem: 'Which statement best describes palatal rugae patterns for estimating sex?',
    options: mc('They assign sex reliably in any population',
      'They are unique to a person but overlap between the sexes',
      'They change predictably after puberty, which allows sex to be estimated',
      'They estimate sex more accurately than the pelvis or the skull') },
  { key: 'KQ10', type: 'single', options: TF,
    stem: 'Sex chromatin (Barr body) microscopy of pulp cells is today the first-line method of estimating sex from teeth.' },
  { key: 'KQ11', type: 'single', stem: 'In amelogenin PCR sex typing, what makes a biological male give a female (X-only) result?',
    options: mc('Deletion of the AMELX gene on the X chromosome', 'A point mutation in the ZFX gene',
      'Deletion of the AMELY region on the Y chromosome', 'Degradation of autosomal microsatellites') },
  { key: 'KQ12', type: 'single', stem: 'An amelogenin assay gives an X-only profile in a man. Which marker confirms the sex?',
    options: mc('The DQA1 locus', 'The Rh factor gene', 'The mitochondrial HVI region', 'The SRY gene or Y-STR loci') },
];

// Attitude — 8 items. Reverse-coded (negatively worded): AQ2, AQ4, AQ5 (handled server-side).
const attitude = [
  { key: 'AQ1', type: 'likert',
    stem: 'Estimating sex from teeth and jaws is a competency every dental graduate should have.' },
  { key: 'AQ2', type: 'likert',
    stem: 'Measuring teeth is too tedious and time-consuming to be of practical use in medico-legal work.' },
  { key: 'AQ3', type: 'likert',
    stem: 'Dental undergraduates should receive hands-on training in dental measurement and in sampling teeth for DNA.' },
  { key: 'AQ4', type: 'likert',
    stem: 'Helping the police estimate sex from dental remains falls outside the role of a general dental practitioner.' },
  { key: 'AQ5', type: 'likert',
    stem: 'Forensic sex estimation concerns postgraduates only and has no place in undergraduate dental training.' },
  { key: 'AQ6', type: 'likert',
    stem: 'Estimating sex from dental remains makes a real contribution to identifying victims of disasters.' },
  { key: 'AQ7', type: 'likert',
    stem: 'Dentists should work with forensic pathologists and the police when unidentified remains are found.' },
  { key: 'AQ8', type: 'likert',
    stem: 'The dental records kept in routine practice have real medico-legal value for identification.' },
];

// Self-efficacy / confidence — 5 items, one per core competency. Summed (max 25),
// no reverse-coding; reported separately from attitude.
const selfEfficacy = [
  { key: 'SE1', type: 'likert',
    stem: 'I feel confident describing the features that distinguish male from female jaws and skulls.' },
  { key: 'SE2', type: 'likert',
    stem: 'I feel confident measuring mesiodistal canine width and calculating the mandibular canine index.' },
  { key: 'SE3', type: 'likert',
    stem: 'I feel confident explaining why an index derived in one population may not apply to another.' },
  { key: 'SE4', type: 'likert',
    stem: 'I feel confident explaining how a tooth should be sampled and preserved for DNA analysis.' },
  { key: 'SE5', type: 'likert',
    stem: 'I feel confident recognising a false-female amelogenin result and knowing what to do next.' },
];

// Practice, in three parts:
//  • PQ1–PQ7 — self-reported frequency (descriptive, NOT scored).
//  • V1–V3   — applied vignettes, one defensible answer each, scored via the practice
//              key (max 3); answerable at any stage of training.
//  • IN1–IN2 — behavioural intention (descriptive Likert) for pre-clinical respondents.
const practice = [
  { key: 'PQ1', type: 'single',
    stem: 'How often do you record tooth morphology or anatomical variations in a patient’s chart?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ2', type: 'single',
    stem: 'Have you ever measured crown width with callipers, on a cast or in the mouth?',
    options: same('Yes', 'No', 'Not sure') },
  { key: 'PQ3', type: 'single',
    stem: 'How often do you examine mandibular radiographs (IOPA, OPG or CBCT) for morphological features?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ4', type: 'single',
    stem: 'Have you attended a forensic odontology workshop, demonstration or autopsy session?',
    options: same('Yes', 'No', 'Not sure') },
  { key: 'PQ5', type: 'single',
    stem: 'How often do you check a textbook or guideline before accepting a claim about sex estimation?',
    options: same('Always', 'Often', 'Sometimes', 'Rarely', 'Never') },
  { key: 'PQ6', type: 'single',
    stem: 'Have you ever preserved an extracted tooth for DNA analysis or research under supervision?',
    options: [...same('Yes', 'No', 'Not sure'), NOT_YET_SEEING] },
  { key: 'PQ7', type: 'single',
    stem: 'How often do you complete a full dental chart that could later be used to identify a patient?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NOT_YET_SEEING] },
  {
    key: 'V1',
    type: 'single',
    stem: 'Police recover the skeletonised remains of a child of about ten years. They ask for an estimate of biological sex from the skeleton and dentition. The most reliable approach is:',
    options: same(
      'Extract genomic DNA from dental pulp for amelogenin PCR and confirmatory Y-marker testing.',
      'Measure the skull and mandible and apply adult discriminant functions.',
      'Calculate the mandibular canine index from the erupting canines.',
      'Compare the palatal rugae pattern on a dental cast.',
    ),
  },
  {
    key: 'V2',
    type: 'single',
    stem: 'Amelogenin PCR on an unknown adult body gives a single X band, but the pelvis strongly indicates a male. The next step is:',
    options: same(
      'Perform secondary Y-marker amplification (SRY gene or Y-STR loci) to test for an AMELY deletion.',
      'Record the individual as female and set the pelvic findings aside.',
      'Repeat the amelogenin assay with twice the amount of DNA.',
      'Report the sex as indeterminate, because the two findings cannot be reconciled.',
    ),
  },
  {
    key: 'V3',
    type: 'single',
    stem: 'From an adult mandible you calculate a mandibular canine index of 0.285, above the standard value of 0.274 for that population. Your report should:',
    options: same(
      'Report the individual as probably male, stating the accuracy of the method in this population.',
      'Report the individual as male, since the index is above the standard value.',
      'Recalculate the index from the maxillary canines before reporting anything.',
      'State that sex cannot be estimated, because one dental measurement has no evidential value.',
    ),
  },
  {
    key: 'IN1',
    type: 'likert',
    stem: 'When I begin clinical practice, I intend to keep dental records complete enough to help identify a patient.',
  },
  {
    key: 'IN2',
    type: 'likert',
    stem: 'I intend to attend continuing education on forensic odontology when it is available to me.',
  },
];

const education = [
  {
    id: 'sex-hierarchy',
    title: 'Which evidence is most reliable',
    points: [
      'The adult pelvis estimates sex most accurately (about 95%), then the skull and mandible; teeth support them rather than replace them.',
      'Skeletal sex differences appear after puberty, so they are unreliable in children. Crown size, by contrast, is fixed once the crown has formed.',
      'The mandibular canine shows the largest sex difference in crown width, and the canine distal accessory ridge is the most dimorphic non-metric trait.',
      'Report sex as a probability together with the accuracy of the method, never as a certainty.',
    ],
    tip: 'Pelvis first, skull next, teeth in support — and report a probability, never a certainty.',
  },
  {
    id: 'sex-measure',
    title: 'Measuring teeth, and the limits of soft tissue',
    points: [
      'The mandibular canine index is the mesiodistal canine width divided by the intercanine arch width.',
      'Cut-off values are population-specific: a standard from one population has to be re-established before it is used in another.',
      'A square or bilobate chin, an everted gonial angle and a taller ramus all point towards a male mandible.',
      'Palatal rugae and lip prints identify an individual well, but they overlap too much between the sexes to estimate sex on their own.',
    ],
    tip: 'Canine width over arch width — and re-calibrate the cut-off for every population.',
  },
  {
    id: 'sex-dna',
    title: 'Teeth protect DNA',
    points: [
      'Enamel and dentine shield the pulp, so dental DNA survives fire, decomposition and trauma that destroy other tissues.',
      'An intact, multi-rooted molar gives the best yield of pulpal DNA.',
      'Barr body and F-body microscopy is obsolete; DNA methods have replaced it.',
      'For a child, or for burnt remains, pulpal DNA is the most reliable route to an estimate of sex.',
    ],
    tip: 'When the skeleton cannot say, the pulp can — sample an intact molar.',
  },
  {
    id: 'sex-amelogenin',
    title: 'Amelogenin and the AMELY trap',
    points: [
      'Amelogenin PCR separates X from Y by fragment size and is the sex test built into most commercial kits.',
      'A deletion of AMELY on the Y chromosome makes a man appear female (X-only). It has been reported in South Asian men — about 3% of ethnic Indian men in a Malaysian study.',
      'When an X-only result conflicts with the skeleton, amplify SRY or Y-STR markers to confirm the sex.',
    ],
    tip: 'X-only result but male bones? Check SRY or Y-STRs before you believe the kit.',
  },
];

export default {
  slug: 'sex',
  code: 'KS07',
  title: 'Effect of a Structured Educational Intervention on the Knowledge, Attitude, and Practice of Sex Determination in Forensic Odontology Among Dental Students and Interns: A Pre–Post Interventional Study',
  shortTitle: 'Sex Estimation',
  studyTitle: 'KAP on Sex Estimation in Forensic Odontology',
  topic: 'Forensic odontology',
  emoji: '⚖️',
  accent: '#5B9DF9',
  estMinutes: 12,
  mode: 'pre-post',
  status: 'live',
  version: '1.0',
  practiceMax: 3, // scored practice = the three applied vignettes (V1–V3); PQ*/IN* are descriptive
  blurb: 'Estimating biological sex from teeth and jaws — what the measurements can and cannot show, and where DNA takes over.',
  sections: { demographics, knowledge, attitude, selfEfficacy, practice },
  education,
  educationTitle: 'Estimating sex from teeth and jaws',
  educationBlurb: 'A two-minute refresher on what teeth, jaws and dental DNA can tell you about sex — and how to report it. Read through, then continue to the short follow-up.',
};
